import { useEffect, useMemo, useRef } from 'react'
import { useEntitiesQuery } from '@/hooks/queries/useEntitiesQuery'
import { useProjectsQuery } from '@/hooks/queries/useProjectsQuery'
import { useFavoritesStore } from '@/store/favorites.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useUserPreferencesStore } from '@/store/user-preferences.store'

export type FavoriteEntityItem = {
  id: string
  name: string
  type: 'entity'
  logoUrl: string | undefined
  href: string
}

export type FavoriteProjectItem = {
  id: string
  name: string
  type: 'project'
  logoUrl: string | undefined
  href: string
  entityName: string | undefined // For projects, the name of the associated entity
  entityId: string | undefined // For projects, the ID of the associated entity
}

export type FavoriteItem = FavoriteEntityItem | FavoriteProjectItem

export function useFavorites({ enabled = true }: { enabled?: boolean } = {}) {
  // Use TanStack Query to fetch entities and projects for favorites display
  const {
    data: entitiesResponse,
    isLoading: isLoadingEntities,
    isPending: isPendingEntities,
  } = useEntitiesQuery(
    {},
    {
      enabled,
    },
  )
  const entities = entitiesResponse?.data || []

  const {
    data: projectsResponse,
    isLoading: isLoadingProjects,
    isPending: isPendingProjects,
  } = useProjectsQuery(
    {},
    {
      enabled,
    },
  )
  const projects = projectsResponse?.data || []

  const { currentOrganization } = useOrganizationStore()
  const { isInitialized: userPrefsInitialized } = useUserPreferencesStore()
  const {
    favoriteEntities, // Just the IDs
    favoriteProjects, // Just the IDs
    favoriteModules,
    lastLoadedOrgId,
    _hasHydrated,
    loadFavorites,
    isLoading: isLoadingFavorites,
    isFavorited,
    toggleFavorite,
  } = useFavoritesStore()

  // The favorites store is persisted via Zustand persist middleware
  // We only need to load from server when the organization changes
  // Otherwise, trust the persisted state which includes our optimistic updates
  useEffect(() => {
    const currentOrgId = currentOrganization?.id

    // Wait for both hydration and user prefs to be ready
    if (!enabled || !currentOrgId || !userPrefsInitialized || !_hasHydrated) {
      return
    }

    // Only load if we haven't loaded for this org yet, or if the org changed
    if (lastLoadedOrgId !== currentOrgId) {
      loadFavorites()
    }
  }, [
    enabled,
    loadFavorites,
    currentOrganization?.id,
    userPrefsInitialized,
    lastLoadedOrgId,
    _hasHydrated,
  ])

  // The store is persisted globally (not per-org), so right after an org
  // switch it still holds the previous org's favorites until loadFavorites()
  // syncs from the new org's preferences. Hide favorites until that sync
  // lands so we never render another org's records.
  const isSyncedForCurrentOrg =
    !!currentOrganization?.id && lastLoadedOrgId === currentOrganization.id

  // DERIVE favorite items from query data and favorite IDs
  // This ensures we always have fresh data from queries
  const favoriteEntityItems = useMemo(() => {
    if (!isSyncedForCurrentOrg || !entities.length) return []

    return favoriteEntities
      .map(entityId => {
        const entity = entities.find(e => e.id === entityId)
        if (!entity) return null

        return {
          id: entity.id,
          name: entity.name,
          type: 'entity' as const,
          logoUrl: entity.logoUrl,
          href: `/entities/${entity.id}`,
        }
      })
      .filter((item): item is FavoriteEntityItem => item !== null)
  }, [entities, favoriteEntities, isSyncedForCurrentOrg])

  const favoriteProjectItems = useMemo(() => {
    if (!isSyncedForCurrentOrg || !projects.length) return []

    return favoriteProjects
      .map(projectId => {
        const project = projects.find(p => p.id === projectId)
        if (!project) return null

        return {
          id: project.id,
          name: project.name,
          type: 'project' as const,
          logoUrl: project.logoUrl,
          href: `/projects/${project.id}`,
          entityName: (project as any).entity?.name,
          entityId: (project as any).entity?.id,
        }
      })
      .filter((item): item is FavoriteProjectItem => item !== null)
  }, [projects, favoriteProjects, isSyncedForCurrentOrg])

  // Pending counts too: a query waiting on the org or the persisted cache is
  // not fetching yet, and favorites can't resolve to records without it.
  const isLoading =
    isLoadingFavorites ||
    isLoadingEntities ||
    isLoadingProjects ||
    (enabled && (isPendingEntities || isPendingProjects)) ||
    !isSyncedForCurrentOrg

  return {
    favoriteEntities: favoriteEntityItems,
    favoriteProjects: favoriteProjectItems,
    favoriteModules: isSyncedForCurrentOrg ? favoriteModules : [],
    isFavorited,
    toggleFavorite,
    isLoading,
  }
}
